import { Button, TableItem } from 'components/atoms';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { resources } from 'locales/i18n';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiMagicWand, PiWarningOctagon } from 'react-icons/pi';
import { useGetCountries } from 'services/enums';
import { Column } from 'types/components/table/table.types';

enum SORT_COLUMNS {
  code,
  en,
  pl,
}

const Countries = () => {
  const { t } = useTranslation();

  const columnsVisibility = useTableColumnsVisibility(
    'CountriesDevTools',
    SORT_COLUMNS
  );

  const countries = useGetCountries();

  const countryNodes = useMemo(
    () =>
      countries.data?.map(e => ({
        id: e.codeAlfa2,
        codeAlfa2: e.codeAlfa2,
        codeAlfa3: e.codeAlfa3,
      })) ?? [],
    [countries.data]
  );
  const appLanguages = useMemo<string[]>(
    () => Object.keys(resources),
    [resources]
  );

  const onClickToCopy = (title: string, value: string) => {
    toast.success(`${title} ${value} has been copied to clipboard!`);
    navigator.clipboard.writeText(value);
  };

  const getTranslation = (lng: string, id: string) =>
    t(`countries.${id}`, { lng: lng });

  const getMissingCountries = () => {
    const missingCountriesByLanguage = appLanguages.map(l => ({
      [l]: countryNodes
        .filter(e => getTranslation(l, e.id).includes('countries.'))
        .map(e => e.id),
    }));

    console.log('Missing countries translations:', missingCountriesByLanguage);
    toast.success(`Check the console for result`);
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={`Countries list (${countries.data?.length ?? 0})`}
          hideBackButton
          topbarContent={
            <>
              <Button
                disabled={countries.isLoading}
                isClear
                onlyIcon
                onClick={getMissingCountries}>
                <Button.Icon icon={<PiMagicWand />} />
              </Button>
              <Table.CustomColumnsButton
                columns={SORT_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix=' '
                customI18next=' '
              />
            </>
          }
        />
      }>
      <Table
        id={columnsVisibility.tableId}
        loading={countries.isLoading}
        size='compact'
        // displayMode
        data={{
          nodes: countryNodes,
        }}
        columns={[
          {
            label: 'Code',
            renderCell: ({ codeAlfa2, codeAlfa3 }) => (
              <TableItem
                className='cursor-pointer'
                onClick={() => onClickToCopy('codeAlfa2', codeAlfa2)}>
                <TableItem.Text text={`${codeAlfa2} / ${codeAlfa3}`} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.code),
          },
          ...appLanguages.map<Column<{ id: string }>>((l, key) => ({
            label: l.toUpperCase(),
            renderCell: ({ id }) => (
              <TableItem>
                {getTranslation(l, id).includes('countries.') ? (
                  <TableItem.Label color='red' size='compact'>
                    <PiWarningOctagon /> NOT FOUND
                  </TableItem.Label>
                ) : (
                  <TableItem.Text text={getTranslation(l, id)} />
                )}
              </TableItem>
            ),
            resize: key !== appLanguages.length - 1,
            hide: columnsVisibility.hide(
              SORT_COLUMNS[l as keyof typeof SORT_COLUMNS]
            ),
          })),
        ]}
      />
    </TopbarPage>
  );
};

export default Countries;
